import type { ExtractionFamille, RoleCode } from './contrat.js';

export interface PersonneProvisoire {
  ref: string;
  nom: string;
  hypothese: boolean;
}

/**
 * `hypothese` : proposé par le vocal, rien au dossier. `enregistre` : validé par Brigitte et écrit au dossier,
 * chaque personne restant une hypothèse tant que le serveur ne l'a pas confirmée (règle L32 : l'hypothèse ne se
 * lève que par la confirmation d'identité sur pièce, jamais par cette validation).
 */
export interface ArbreProvisoire {
  statut: 'hypothese' | 'enregistre';
  personnes: PersonneProvisoire[];
  liens: ExtractionFamille['links'];
}

/** Rôles qui valident l'arbre : Brigitte (assistante), puis la direction. */
const VALIDEURS: readonly RoleCode[] = ['assistante', 'dirigeante', 'admin'];

/** Tout ce qu'un vocal propose est une hypothèse, quoi qu'en dise l'extraction : seule une validation humaine la lève. */
export const construireArbreProvisoire = (extraction: ExtractionFamille): ArbreProvisoire => ({
  statut: 'hypothese',
  personnes: extraction.persons.map((p) => ({ ref: p.ref, nom: p.displayName, hypothese: true })),
  liens: extraction.links,
});

export function accuseArbre(arbre: ArbreProvisoire): string {
  const n = arbre.personnes.length;
  if (n === 0) return 'Aucune personne n’a pu être repérée dans ce vocal. Réécoutez-le ou complétez l’arbre à la main. Cabinet MAAT';
  const quoi = n === 1 ? '1 personne proposée' : `${n} personnes proposées`;
  return `Voici un début d’arbre : ${quoi} d’après le vocal. Ce sont des hypothèses, à valider par Brigitte avant toute utilisation. Cabinet MAAT`;
}

export const peutValiderArbre = (role: RoleCode): boolean => VALIDEURS.includes(role);

/**
 * Validation par Brigitte : l'arbre est enregistré au dossier, sans lever l'hypothèse. Une personne n'est
 * confirmée que si le serveur l'a renvoyée confirmée (`confirmeesParServeur`), jamais par défaut.
 */
export function validerArbre(
  arbre: ArbreProvisoire,
  role: RoleCode,
  confirmeesParServeur: ReadonlySet<string> = new Set(),
): ArbreProvisoire {
  if (!peutValiderArbre(role)) throw new Error('Rôle non habilité à valider l’arbre');
  return { ...arbre, statut: 'enregistre', personnes: arbre.personnes.map((p) => ({ ...p, hypothese: !confirmeesParServeur.has(p.ref) })) };
}
